(() => {
  /* Prevent duplicate global listeners when Shopify reloads sections. */
  if (window.AngleLanding) {
    window.AngleLanding.init(document);
    return;
  }

  const instances = new Map();

  function createPage(root) {
    const controller = new AbortController();
    const signal = controller.signal;
    const cleanups = [];

    const listen = (element, event, handler, options = {}) => {
      if (element) {
        element.addEventListener(event, handler, {
          ...options,
          signal
        });
      }
    };

    /* Product collection tabs */
    const products = root.querySelector("[data-angle-products]");

    if (products) {
      const tabList = products.querySelector("[data-angle-tabs]");
      const tabs = [...products.querySelectorAll("[data-angle-tab]")];
      const panels = [...products.querySelectorAll("[data-angle-panel]")];

      if (tabList && tabs.length) {
        tabList.setAttribute("role", "tablist");

        function selectTab(index, focus = false) {
          tabs.forEach((tab, i) => {
            const active = i === index;

            tab.classList.toggle("is-active", active);
            tab.setAttribute("aria-selected", String(active));
            tab.tabIndex = active ? 0 : -1;

            const panel = panels.find(
              item => item.id === tab.dataset.panel
            );

            if (panel) panel.hidden = !active;
          });

          if (focus) tabs[index].focus();
        }

        tabs.forEach((tab, index) => {
          tab.setAttribute("role", "tab");
          tab.setAttribute("aria-controls", tab.dataset.panel);

          const panel = panels.find(
            item => item.id === tab.dataset.panel
          );

          if (panel) {
            panel.setAttribute("role", "tabpanel");
            panel.tabIndex = 0;
          }

          listen(tab, "click", () => selectTab(index));

          listen(tab, "keydown", event => {
            let next = index;

            if (event.key === "ArrowRight") {
              next = (index + 1) % tabs.length;
            } else if (event.key === "ArrowLeft") {
              next = (index - 1 + tabs.length) % tabs.length;
            } else if (event.key === "Home") {
              next = 0;
            } else if (event.key === "End") {
              next = tabs.length - 1;
            } else {
              return;
            }

            event.preventDefault();
            selectTab(next, true);
          });
        });

        products.classList.add("is-enhanced");
        selectTab(0);
      }
    }

    /* Featured product carousel */
    const carousel = root.querySelector("[data-angle-carousel]");

    if (carousel) {
      const slides = [...carousel.querySelectorAll("[data-angle-slide]")];
      const dots = [...carousel.querySelectorAll("[data-angle-dot]")];
      const controls = carousel.querySelector("[data-angle-controls]");
      const pauseButton = carousel.querySelector("[data-angle-pause]");
      const slideContainer = carousel.querySelector(".angle-featured__slides");

      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );

      const autoplay = root.dataset.autoplay === "true";
      const interval = Math.max(
        3000,
        Number(root.dataset.interval) || 5000
      );

      let index = 0;
      let timer = null;
      let manuallyPaused = !autoplay;
      let hovering = false;
      let focused = false;
      let editorPaused = false;
      let touching = false;
      let touchStart = null;
      let suppressClick = false;
      let frame = null;

      function stopTimer() {
        window.clearTimeout(timer);
        timer = null;
      }

      function canPlay() {
        return (
          slides.length > 1 &&
          !manuallyPaused &&
          !hovering &&
          !focused &&
          !editorPaused &&
          !touching &&
          !document.hidden &&
          !reducedMotion.matches
        );
      }

      function updatePauseButton() {
        if (!pauseButton) return;

        const paused = manuallyPaused || reducedMotion.matches;
        pauseButton.textContent = paused ? "▶" : "Ⅱ";
        pauseButton.setAttribute(
          "aria-label",
          paused ? "Avvia riproduzione" : "Pausa riproduzione"
        );
        pauseButton.disabled = reducedMotion.matches;
      }

      function schedule() {
        stopTimer();
        updatePauseButton();

        if (canPlay()) {
          timer = window.setTimeout(() => {
            show(index + 1);
          }, interval);
        }
      }

      function show(next) {
        if (!slides.length) return;

        index = (next + slides.length) % slides.length;

        slides.forEach((slide, i) => {
          const active = i === index;
          slide.hidden = !active;
          slide.classList.toggle("is-active", active);
        });

        dots.forEach((dot, i) => {
          if (i === index) {
            dot.setAttribute("aria-current", "true");
          } else {
            dot.removeAttribute("aria-current");
          }
        });

        schedule();
      }

      /* Reserve the tallest slide height to prevent layout jumping. */
      function measureSlides() {
        if (!slideContainer || !root.isConnected) return;

        const width = slideContainer.getBoundingClientRect().width;
        if (!width) return;

        let maximum = 0;

        slides.forEach(slide => {
          const previousHidden = slide.hidden;
          const previousStyle = slide.getAttribute("style");

          slide.hidden = false;
          slide.style.position = "absolute";
          slide.style.visibility = "hidden";
          slide.style.pointerEvents = "none";
          slide.style.width = `${width}px`;

          maximum = Math.max(
            maximum,
            slide.getBoundingClientRect().height
          );

          if (previousStyle === null) {
            slide.removeAttribute("style");
          } else {
            slide.setAttribute("style", previousStyle);
          }

          slide.hidden = previousHidden;
        });

        slideContainer.style.minHeight = `${Math.ceil(maximum)}px`;
      }

      function requestMeasure() {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(measureSlides);
      }

      if (controls) controls.hidden = slides.length < 2;

      listen(carousel.querySelector("[data-angle-prev]"), "click", () => {
        show(index - 1);
      });

      listen(carousel.querySelector("[data-angle-next]"), "click", () => {
        show(index + 1);
      });

      dots.forEach(dot => {
        listen(dot, "click", () => {
          show(Number(dot.dataset.angleDot));
        });
      });

      listen(pauseButton, "click", () => {
        manuallyPaused = !manuallyPaused;
        schedule();
      });

      listen(carousel, "mouseenter", () => {
        if (!window.matchMedia("(hover: hover)").matches) return;
        hovering = true;
        schedule();
      });

      listen(carousel, "mouseleave", () => {
        hovering = false;
        schedule();
      });

      listen(carousel, "focusin", () => {
        focused = true;
        schedule();
      });

      listen(carousel, "focusout", event => {
        if (!carousel.contains(event.relatedTarget)) {
          focused = false;
          schedule();
        }
      });

      listen(document, "visibilitychange", schedule);
      listen(reducedMotion, "change", schedule);

      /* Touch swipe, preserving normal vertical page scrolling. */
      listen(carousel, "touchstart", event => {
        if (event.touches.length !== 1) {
          touchStart = null;
          return;
        }

        const touch = event.touches[0];
        touchStart = { x: touch.clientX, y: touch.clientY };
        touching = true;
        schedule();
      }, { passive: true });

      listen(carousel, "touchend", event => {
        touching = false;

        if (touchStart && event.changedTouches.length) {
          const touch = event.changedTouches[0];
          const dx = touch.clientX - touchStart.x;
          const dy = touch.clientY - touchStart.y;

          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) {
            suppressClick = true;
            show(index + (dx < 0 ? 1 : -1));
          }
        }

        touchStart = null;
        schedule();
      }, { passive: true });

      listen(carousel, "touchcancel", () => {
        touching = false;
        touchStart = null;
        schedule();
      }, { passive: true });

      listen(carousel, "click", event => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
        }
      }, { capture: true });

      listen(carousel, "touchstart", () => {
        suppressClick = false;
      }, { passive: true });

      listen(window, "resize", requestMeasure);
      listen(carousel, "load", requestMeasure, { capture: true });

      if (window.ResizeObserver && slideContainer) {
        let previousWidth = 0;
        const observer = new ResizeObserver(entries => {
          const width = entries[0].contentRect.width;

          if (Math.abs(width - previousWidth) > 1) {
            previousWidth = width;
            requestMeasure();
          }
        });

        observer.observe(slideContainer);
        cleanups.push(() => observer.disconnect());
      }

      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (!signal.aborted) requestMeasure();
        });
      }

      /* Reveal a selected slide inside Shopify's theme editor. */
      listen(root, "shopify:block:select", event => {
        const slide = event.target.closest("[data-angle-slide]");
        const selectedIndex = slides.indexOf(slide);

        if (selectedIndex >= 0) {
          editorPaused = true;
          show(selectedIndex);
        }
      });

      listen(root, "shopify:block:deselect", () => {
        editorPaused = false;
        schedule();
      });

      show(0);
      requestMeasure();

      cleanups.push(() => {
        stopTimer();
        window.cancelAnimationFrame(frame);
      });
    }

    /* Smooth scrolling for links within this landing page. */
    listen(root, "click", event => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;

      const id = link.getAttribute("href").slice(1);
      const target = document.getElementById(id);

      if (target && root.contains(target)) {
        event.preventDefault();

        target.scrollIntoView({
          behavior: window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches ? "auto" : "smooth",
          block: "start"
        });
      }
    });

    return () => {
      controller.abort();
      cleanups.forEach(cleanup => cleanup());
    };
  }

  function init(scope) {
    const roots = [];

    if (scope.matches?.("[data-angle-page]")) roots.push(scope);
    roots.push(...scope.querySelectorAll("[data-angle-page]"));

    roots.forEach(root => {
      if (instances.has(root)) return;
      instances.set(root, createPage(root));
    });
  }

  function destroy(scope) {
    instances.forEach((cleanup, root) => {
      if (root === scope || scope.contains(root)) {
        cleanup();
        instances.delete(root);
      }
    });
  }

  window.AngleLanding = { init };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), {
      once: true
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", event => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", event => {
    destroy(event.target);
  });
})();